import { useEffect, useState } from "react";
import { api } from "../services/api";
import SellerNavbar from "../components/SellerNavbar";
import "./SellerOrders.css";

export default function SellerOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Seller Orders | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Track customer orders on Balitah, a local Bruneian e-commerce platform for sellers.",
    );
  }, []);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const data = await api.getSellerOrders();

        if (!data.success) {
          alert(data.message || "Failed to fetch seller orders");
          return;
        }

        setOrders(data.data.orders || []);
      } catch (error) {
        console.error("Failed to fetch seller orders:", error);
        alert("Failed to fetch seller orders");
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: "Pending" },
      processing: { label: "Processing" },
      shipped: { label: "Shipped" },
      delivered: { label: "Delivered" },
      cancelled: { label: "Cancelled" },
    };
    return configs[status] || { label: status };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="so-page">
      <SellerNavbar />

      <div className="so-container">
        {/* Page Header */}
        <div className="so-header">
          <div className="so-header-content">
            <h1>Orders</h1>
            <p>Track orders for your products</p>
          </div>
          {orders.length > 0 && (
            <div className="so-header-stats">
              <div className="so-stat">
                <span className="so-stat-number">{orders.length}</span>
                <span className="so-stat-label">Total Orders</span>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="so-loading">
            <div className="so-spinner"></div>
            <p>Loading your orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="so-empty">
            <div className="so-empty-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M16 11V7a4 4 0 0 0-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h2>No orders yet</h2>
            <p>Looks like no one has ordered your products yet</p>
          </div>
        ) : (
          <div className="so-orders-list">
            {orders.map((order) => {
              const statusConfig = getStatusConfig(order.status);
              return (
                <div key={order._id} className="so-order-card">
                  <div className="so-order-header">
                    <div className="so-order-info">
                      <div className="so-order-id">
                        Order #{order._id.slice(-8).toUpperCase()}
                      </div>
                      <div className="so-order-meta">
                        <div className="so-order-meta-item">
                          <span className="so-order-meta-label">Buyer:</span>{" "}
                          {order.buyer?.name || "Unknown"}
                        </div>
                        <div className="so-order-meta-item">
                          <span className="so-order-meta-label">Date:</span>{" "}
                          {formatDate(order.createdAt)}
                        </div>
                        <div className="so-order-meta-item">
                          <span className="so-order-meta-label">Payment:</span>{" "}
                          {order.paymentMethod}
                        </div>
                      </div>
                    </div>

                    <div className="so-order-right">
                      <div className={`so-status-badge ${order.status}`}>
                        {statusConfig.label}
                      </div>
                      <div className="so-order-amount">
                        BND {order.sellerTotal.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="so-order-items">
                    <table className="so-items-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Variation</th>
                          <th>Qty</th>
                          <th>Price</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item) => (
                          <tr key={item._id}>
                            <td>{item.name}</td>
                            <td className="so-variation-cell">
                              {item.variantName || "-"}
                            </td>
                            <td>{item.quantity}</td>
                            <td>BND {item.price.toFixed(2)}</td>
                            <td>BND {item.subtotal.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="so-order-footer">
                    <p className="so-shipping-address">Shipping Address:</p>
                    <p className="so-shipping-details">
                      {order.shippingAddress.street},{" "}
                      {order.shippingAddress.city},{order.shippingAddress.state}
                      , {order.shippingAddress.postalCode},
                      {order.shippingAddress.country}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
